import { useCallback, useEffect, useState } from 'react';
import apiClient from '../../shared/auth.interceptor';
import { toast } from 'react-toastify';
import { IUser } from '../../user/user.interface';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { firmRefresh } from '../../shared/auth.functions';
import { syncFirms } from '../../shared/redux/syncFirms';

export enum UserRoles {
  super_admin = 'super_admin',
  admin = 'admin',
  simple_user = 'user'
}

export const getRoleName = (code: UserRoles | string): string => {
  switch (code) {
    case UserRoles.super_admin:
    case 'super_admin':
      return 'Super Admin';

    case UserRoles.admin:
    case 'admin':
      return 'Admin';

    default:
      return 'User';
  }
};

// Добавляем параметр refreshTrigger!
export const useFetchUsers = (firmId: string | unknown, refreshTrigger = 0) => {
  const baseURL: string = process.env.REACT_APP_BASE_URL || '';
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [loadedFirmId, setLoadedFirmId] = useState<unknown>(null);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setUsers([]);
    setError(null);
    setLoadedFirmId(firmId);
    if (!firmId) {
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    apiClient
      .get<IUser[]>(baseURL + `/v1/firm/${firmId}/users`, { signal: controller.signal })
      .then((response) => {
        if (active) setUsers(response.data);
      })
      .catch((err) => {
        if (active) setError(err.message || 'Failed to fetch users');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      controller.abort();
    };
  }, [baseURL, firmId, refreshTrigger]);

  return {
    users: loadedFirmId === firmId ? users : [],
    loading: Boolean(firmId) && (loadedFirmId !== firmId || loading),
    error: loadedFirmId === firmId ? error : null
  };
};

export const useDeleteUser = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [errorDelete, setErrorDelete] = useState<string | null>(null);
  const [modal, setModal] = useState<{
    open: boolean;
    userId: string | null;
    firmId?: string | null;
  }>({ open: false, userId: null, firmId: null });

  // Открыть модалку подтверждения
  const requestDelete = useCallback((userId: string, firmId?: string) => {
    setModal({ open: true, userId, firmId });
  }, []);

  // Подтвердить удаление (YES)
  const confirmDelete = useCallback(async () => {
    if (!modal.userId || !modal.firmId) return false;
    setLoadingDelete(true);
    setErrorDelete(null);
    try {
      await apiClient.patch(`/v1/firm/unassign-clients/${modal.firmId}`, {
        userId: modal.userId
      });
      dispatch(syncFirms(await firmRefresh()));
      toast.success(`User successfully removed from current firm`);
      setModal({ open: false, userId: null });
      setLoadingDelete(false);
      return true;
    } catch (e: any) {
      setErrorDelete(e?.response?.data?.message || 'Failed to remove user');
      setLoadingDelete(false);
      return false;
    }
  }, [dispatch, modal.userId, modal.firmId]);

  // Отклонить удаление (NO)
  const declineDelete = useCallback(() => {
    setModal({ open: false, userId: null });
  }, []);

  return { requestDelete, confirmDelete, declineDelete, loadingDelete, errorDelete, modal };
};

export const useResendInvite = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resendInvite = useCallback(async (email: string) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await apiClient.post('/v1/user/resend-invite', { email });
      toast.success(`Invite sent again to ${email}`);
      setSuccess(true);
    } catch (e: any) {
      const message = e?.response?.data?.message || 'The invite could not be sent';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return { resendInvite, loading, error, success };
};
